import type { DocumentPageSize, PrintFormatDocumentV1 } from './types'

export type DocumentInvoicePreset = 'restaurant' | 'bakery' | 'minimal'

export type InvoiceFieldSource =
  | 'place.companyName'
  | 'place.outletName'
  | 'place.address'
  | 'place.social.instagram'
  | 'place.social.facebook'
  | 'place.social.tiktok'
  | 'place.social.whatsapp'
  | 'place.social.website'
  | 'place.social.other'
  | 'bill.customerName'
  | 'bill.customerAddress'
  | 'bill.customerPhone'
  | 'bill.createdAt'
  | 'bill.id'
  | 'order.personInCharge'
  | 'manual.placeExtra'
  | 'manual.customerExtra'
  | 'computed.subtotal'
  | 'computed.grandTotal'

export interface InvoiceFormatField {
  id: string
  enabled: boolean
  label: string
  source: InvoiceFieldSource
  /** For manual.* fields: optional default when bill has no value. */
  defaultText?: string
}

export interface InvoiceLineColumnLabels {
  item: string
  qty: string
  unitPrice: string
  lineTotal: string
}

export interface InvoiceLineColumns {
  item: boolean
  qty: boolean
  unitPrice: boolean
  lineTotal: boolean
}

export interface InvoiceTotalsRow {
  id: string
  enabled: boolean
  label: string
  kind: 'adjustments' | 'manual'
}

export interface InvoiceTotalsSection {
  enabled: boolean
  subtotal: { enabled: boolean; label: string }
  extraRows: InvoiceTotalsRow[]
  grandTotal: { enabled: boolean; label: string }
}

export interface PrintFormatDocumentV2 {
  version: 2
  preset?: DocumentInvoicePreset
  pageSize: DocumentPageSize
  marginMm: number
  header: {
    enabled: boolean
    showLogo: boolean
    showBusinessName: boolean
    /** When false, omit the automatic INVOICE/RECEIPT heading. Defaults to true. */
    showDocumentTitle: boolean
    customLines: string[]
  }
  placeColumn: {
    enabled: boolean
    fields: InvoiceFormatField[]
  }
  customerColumn: {
    enabled: boolean
    fields: InvoiceFormatField[]
  }
  lineItems: {
    enabled: boolean
    showOrderHeadings: boolean
    columns: InvoiceLineColumns
    columnLabels: InvoiceLineColumnLabels
  }
  totals: InvoiceTotalsSection
  footer: {
    enabled: boolean
    lines: string[]
  }
  advancedTemplate?: string | null
}

const SOURCES: InvoiceFieldSource[] = [
  'place.companyName',
  'place.outletName',
  'place.address',
  'place.social.instagram',
  'place.social.facebook',
  'place.social.tiktok',
  'place.social.whatsapp',
  'place.social.website',
  'place.social.other',
  'bill.customerName',
  'bill.customerAddress',
  'bill.customerPhone',
  'bill.createdAt',
  'bill.id',
  'order.personInCharge',
  'manual.placeExtra',
  'manual.customerExtra',
  'computed.subtotal',
  'computed.grandTotal',
]

function field(
  id: string,
  enabled: boolean,
  label: string,
  source: InvoiceFieldSource,
  defaultText?: string,
): InvoiceFormatField {
  return { id, enabled, label, source, ...(defaultText ? { defaultText } : {}) }
}

function defaultPlaceFields(): InvoiceFormatField[] {
  return [
    field('company', true, 'Company', 'place.companyName'),
    field('outlet', true, 'Outlet', 'place.outletName'),
    field('address', true, 'Address', 'place.address'),
    field('ig', false, 'IG', 'place.social.instagram'),
    field('fb', false, 'Facebook', 'place.social.facebook'),
    field('tiktok', false, 'TikTok', 'place.social.tiktok'),
    field('wa', false, 'WhatsApp', 'place.social.whatsapp'),
    field('web', false, 'Website', 'place.social.website'),
    field('socialOther', false, 'Social', 'place.social.other'),
    field('staff', false, 'Staff in charge', 'order.personInCharge'),
    field('placeExtra', false, 'Notes', 'manual.placeExtra'),
  ]
}

function defaultCustomerFields(): InvoiceFormatField[] {
  return [
    field('name', true, 'Customer', 'bill.customerName'),
    field('address', false, 'Address', 'bill.customerAddress'),
    field('phone', false, 'Contact', 'bill.customerPhone'),
    field('date', true, 'Date', 'bill.createdAt'),
    field('billId', false, 'Bill', 'bill.id'),
    field('custExtra', false, 'Notes', 'manual.customerExtra'),
  ]
}

export function defaultPrintFormatDocumentV2(
  preset: DocumentInvoicePreset = 'restaurant',
): PrintFormatDocumentV2 {
  const base: PrintFormatDocumentV2 = {
    version: 2,
    preset,
    pageSize: 'a4',
    marginMm: 12,
    header: {
      enabled: true,
      showLogo: false,
      showBusinessName: true,
      showDocumentTitle: true,
      customLines: [],
    },
    placeColumn: { enabled: true, fields: defaultPlaceFields() },
    customerColumn: { enabled: true, fields: defaultCustomerFields() },
    lineItems: {
      enabled: true,
      showOrderHeadings: true,
      columns: { item: true, qty: true, unitPrice: true, lineTotal: true },
      columnLabels: {
        item: 'Item',
        qty: 'Qty',
        unitPrice: 'Price',
        lineTotal: 'Total',
      },
    },
    totals: {
      enabled: true,
      subtotal: { enabled: true, label: 'Subtotal' },
      extraRows: [
        { id: 'adjustments', enabled: true, label: 'Additional charges', kind: 'adjustments' },
      ],
      grandTotal: { enabled: true, label: 'Total' },
    },
    footer: { enabled: true, lines: [] },
    advancedTemplate: null,
  }
  return applyDocumentInvoicePreset(base, preset)
}

export function applyDocumentInvoicePreset(
  fmt: PrintFormatDocumentV2,
  preset: DocumentInvoicePreset,
): PrintFormatDocumentV2 {
  const next = JSON.parse(JSON.stringify(fmt)) as PrintFormatDocumentV2
  next.preset = preset
  if (preset === 'bakery') {
    next.header.showLogo = true
    next.placeColumn.fields = [
      field('company', true, 'Perusahaan', 'place.companyName'),
      field('outlet', true, 'Outlet', 'place.outletName'),
      field('address', true, 'Alamat', 'place.address'),
      field('ig', true, 'IG', 'place.social.instagram'),
      field('fb', false, 'Facebook', 'place.social.facebook'),
      field('tiktok', false, 'TikTok', 'place.social.tiktok'),
      field('wa', false, 'WhatsApp', 'place.social.whatsapp'),
      field('web', false, 'Website', 'place.social.website'),
      field('socialOther', false, 'Sosmed', 'place.social.other'),
      field('staff', true, 'Staff', 'order.personInCharge'),
      field('placeExtra', false, 'Catatan', 'manual.placeExtra'),
    ]
    next.customerColumn.fields = [
      field('name', true, 'Nama', 'bill.customerName'),
      field('address', true, 'Alamat', 'bill.customerAddress'),
      field('phone', true, 'No. Telp', 'bill.customerPhone'),
      field('date', true, 'Tanggal', 'bill.createdAt'),
      field('billId', false, 'Bill', 'bill.id'),
      field('custExtra', false, 'Catatan', 'manual.customerExtra'),
    ]
    next.lineItems.columnLabels = {
      item: 'Nama Barang',
      qty: 'Qty',
      unitPrice: 'Harga',
      lineTotal: 'Total',
    }
    next.lineItems.showOrderHeadings = false
    next.totals.subtotal.label = 'Subtotal'
    next.totals.extraRows = [
      { id: 'delivery', enabled: true, label: 'Ongkos pengiriman', kind: 'adjustments' },
    ]
    next.totals.grandTotal.label = 'Total'
  } else if (preset === 'minimal') {
    next.placeColumn.enabled = false
    next.customerColumn.fields = [
      field('name', true, 'Customer', 'bill.customerName'),
      field('date', true, 'Date', 'bill.createdAt'),
    ]
    next.lineItems.columns = { item: true, qty: true, unitPrice: false, lineTotal: false }
    next.totals.extraRows = []
  }
  return next
}

export function migrateDocumentInvoiceV1ToV2(v1: PrintFormatDocumentV1): PrintFormatDocumentV2 {
  const v2 = defaultPrintFormatDocumentV2('restaurant')
  v2.pageSize = v1.pageSize
  v2.marginMm = v1.marginMm
  v2.advancedTemplate = v1.advancedTemplate ?? null
  v2.header.enabled = v1.blocks.header
  v2.header.customLines = [...v1.headerLines]
  v2.placeColumn.enabled = v1.blocks.orderMeta
  v2.customerColumn.enabled = v1.blocks.orderMeta
  v2.lineItems.enabled = v1.blocks.lineItems
  v2.totals.enabled = v1.blocks.totals
  v2.footer.enabled = v1.blocks.footer
  v2.footer.lines = [...v1.footerLines]
  if (!v1.blocks.orderMeta) {
    v2.placeColumn.enabled = false
    v2.customerColumn.enabled = false
  }
  return v2
}

function mergeField(def: InvoiceFormatField, raw: unknown): InvoiceFormatField {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Partial<InvoiceFormatField>
  const source = SOURCES.includes(r.source as InvoiceFieldSource)
    ? (r.source as InvoiceFieldSource)
    : def.source
  return {
    id: typeof r.id === 'string' ? r.id : def.id,
    enabled: typeof r.enabled === 'boolean' ? r.enabled : def.enabled,
    label: typeof r.label === 'string' && r.label.trim() ? r.label.trim() : def.label,
    source,
    defaultText:
      typeof r.defaultText === 'string' ? r.defaultText : def.defaultText,
  }
}

function mergeFields(def: InvoiceFormatField[], raw: unknown): InvoiceFormatField[] {
  if (!Array.isArray(raw)) return def
  const byId = new Map(def.map((f) => [f.id, f]))
  const out: InvoiceFormatField[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const id = (item as InvoiceFormatField).id
    const base = byId.get(id) ?? def[0]
    if (!base) continue
    out.push(mergeField(base, item))
    byId.delete(id)
  }
  for (const rest of byId.values()) out.push(rest)
  return out.length ? out : def
}

export function mergePrintFormatDocumentV2(
  def: PrintFormatDocumentV2,
  raw: unknown,
): PrintFormatDocumentV2 {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Partial<PrintFormatDocumentV2>
  if (r.version !== 2) return def
  const pageSize =
    r.pageSize === 'a4' || r.pageSize === 'letter' || r.pageSize === 'a3'
      ? r.pageSize
      : def.pageSize
  const marginMm =
    typeof r.marginMm === 'number' && r.marginMm >= 0 && r.marginMm <= 40
      ? r.marginMm
      : def.marginMm
  const preset =
    r.preset === 'restaurant' || r.preset === 'bakery' || r.preset === 'minimal'
      ? r.preset
      : def.preset

  const h = (r.header && typeof r.header === 'object' ? r.header : {}) as Partial<
    PrintFormatDocumentV2['header']
  >
  const pc = (r.placeColumn && typeof r.placeColumn === 'object' ? r.placeColumn : {}) as Partial<
    PrintFormatDocumentV2['placeColumn']
  >
  const cc = (r.customerColumn && typeof r.customerColumn === 'object'
    ? r.customerColumn
    : {}) as Partial<PrintFormatDocumentV2['customerColumn']>
  const li = (r.lineItems && typeof r.lineItems === 'object' ? r.lineItems : {}) as Partial<
    PrintFormatDocumentV2['lineItems']
  >
  const tot = (r.totals && typeof r.totals === 'object' ? r.totals : {}) as Partial<
    PrintFormatDocumentV2['totals']
  >
  const ft = (r.footer && typeof r.footer === 'object' ? r.footer : {}) as Partial<
    PrintFormatDocumentV2['footer']
  >

  const cols = (li.columns && typeof li.columns === 'object' ? li.columns : {}) as Partial<
    InvoiceLineColumns
  >
  const colLabels = (li.columnLabels && typeof li.columnLabels === 'object'
    ? li.columnLabels
    : {}) as Partial<InvoiceLineColumnLabels>

  return {
    version: 2,
    preset,
    pageSize,
    marginMm,
    header: {
      enabled: typeof h.enabled === 'boolean' ? h.enabled : def.header.enabled,
      showLogo: typeof h.showLogo === 'boolean' ? h.showLogo : def.header.showLogo,
      showBusinessName:
        typeof h.showBusinessName === 'boolean'
          ? h.showBusinessName
          : def.header.showBusinessName,
      showDocumentTitle:
        typeof h.showDocumentTitle === 'boolean'
          ? h.showDocumentTitle
          : def.header.showDocumentTitle,
      customLines: Array.isArray(h.customLines)
        ? h.customLines.filter((x): x is string => typeof x === 'string')
        : def.header.customLines,
    },
    placeColumn: {
      enabled: typeof pc.enabled === 'boolean' ? pc.enabled : def.placeColumn.enabled,
      fields: mergeFields(def.placeColumn.fields, pc.fields),
    },
    customerColumn: {
      enabled: typeof cc.enabled === 'boolean' ? cc.enabled : def.customerColumn.enabled,
      fields: mergeFields(def.customerColumn.fields, cc.fields),
    },
    lineItems: {
      enabled: typeof li.enabled === 'boolean' ? li.enabled : def.lineItems.enabled,
      showOrderHeadings:
        typeof li.showOrderHeadings === 'boolean'
          ? li.showOrderHeadings
          : def.lineItems.showOrderHeadings,
      columns: {
        item: typeof cols.item === 'boolean' ? cols.item : def.lineItems.columns.item,
        qty: typeof cols.qty === 'boolean' ? cols.qty : def.lineItems.columns.qty,
        unitPrice:
          typeof cols.unitPrice === 'boolean'
            ? cols.unitPrice
            : def.lineItems.columns.unitPrice,
        lineTotal:
          typeof cols.lineTotal === 'boolean'
            ? cols.lineTotal
            : def.lineItems.columns.lineTotal,
      },
      columnLabels: {
        item:
          typeof colLabels.item === 'string' && colLabels.item.trim()
            ? colLabels.item.trim()
            : def.lineItems.columnLabels.item,
        qty:
          typeof colLabels.qty === 'string' && colLabels.qty.trim()
            ? colLabels.qty.trim()
            : def.lineItems.columnLabels.qty,
        unitPrice:
          typeof colLabels.unitPrice === 'string' && colLabels.unitPrice.trim()
            ? colLabels.unitPrice.trim()
            : def.lineItems.columnLabels.unitPrice,
        lineTotal:
          typeof colLabels.lineTotal === 'string' && colLabels.lineTotal.trim()
            ? colLabels.lineTotal.trim()
            : def.lineItems.columnLabels.lineTotal,
      },
    },
    totals: {
      enabled: typeof tot.enabled === 'boolean' ? tot.enabled : def.totals.enabled,
      subtotal: {
        enabled:
          tot.subtotal && typeof tot.subtotal === 'object' && typeof tot.subtotal.enabled === 'boolean'
            ? tot.subtotal.enabled
            : def.totals.subtotal.enabled,
        label:
          tot.subtotal &&
          typeof tot.subtotal === 'object' &&
          typeof tot.subtotal.label === 'string' &&
          tot.subtotal.label.trim()
            ? tot.subtotal.label.trim()
            : def.totals.subtotal.label,
      },
      extraRows: Array.isArray(tot.extraRows)
        ? tot.extraRows
            .filter(
              (row): row is InvoiceTotalsRow =>
                row != null &&
                typeof row === 'object' &&
                typeof (row as InvoiceTotalsRow).id === 'string',
            )
            .map((row) => ({
              id: row.id,
              enabled: typeof row.enabled === 'boolean' ? row.enabled : true,
              label: typeof row.label === 'string' && row.label.trim() ? row.label.trim() : 'Fee',
              kind: row.kind === 'manual' ? 'manual' : 'adjustments',
            }))
        : def.totals.extraRows,
      grandTotal: {
        enabled:
          tot.grandTotal &&
          typeof tot.grandTotal === 'object' &&
          typeof tot.grandTotal.enabled === 'boolean'
            ? tot.grandTotal.enabled
            : def.totals.grandTotal.enabled,
        label:
          tot.grandTotal &&
          typeof tot.grandTotal === 'object' &&
          typeof tot.grandTotal.label === 'string' &&
          tot.grandTotal.label.trim()
            ? tot.grandTotal.label.trim()
            : def.totals.grandTotal.label,
      },
    },
    footer: {
      enabled: typeof ft.enabled === 'boolean' ? ft.enabled : def.footer.enabled,
      lines: Array.isArray(ft.lines)
        ? ft.lines.filter((x): x is string => typeof x === 'string')
        : def.footer.lines,
    },
    advancedTemplate:
      r.advancedTemplate === undefined ? def.advancedTemplate : r.advancedTemplate,
  }
}

export type PrintFormatDocument = PrintFormatDocumentV1 | PrintFormatDocumentV2

export function isDocumentInvoiceV2(
  cfg: PrintFormatDocument,
): cfg is PrintFormatDocumentV2 {
  return cfg != null && typeof cfg === 'object' && (cfg as PrintFormatDocumentV2).version === 2
}

export function normalizeDocumentInvoice(cfg: PrintFormatDocument): PrintFormatDocumentV2 {
  if (isDocumentInvoiceV2(cfg)) {
    return {
      ...cfg,
      header: {
        ...cfg.header,
        showDocumentTitle: cfg.header.showDocumentTitle !== false,
      },
    }
  }
  return migrateDocumentInvoiceV1ToV2(cfg)
}

export function mergeFormatDocument(
  def: PrintFormatDocumentV2,
  raw: unknown,
): PrintFormatDocumentV2 {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as { version?: number }
  if (r.version === 2) return mergePrintFormatDocumentV2(def, raw)
  if (r.version === 1) return migrateDocumentInvoiceV1ToV2(raw as PrintFormatDocumentV1)
  return def
}
